import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { combineLatest, ReplaySubject } from 'rxjs';
import { filter, take, takeWhile, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SelectItem } from '../../../shared/efaact-primeng.module';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { FileFormatModel, ThirdPartyPayroll } from '../store';
@Component({
    selector: 'ef-third-party-payroll-export',
    templateUrl: './third-party-payroll-export.component.html',
    styleUrls: ['./third-party-payroll-export.component.scss'],
    standalone: false
})
export class ThirdPartyPayrollExportComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private itemsSubject = new ReplaySubject<ThirdPartyPayroll[]>();
    private PayrollExportFormatSubject = new ReplaySubject<string>(1);
    private _payrollExportFormat: string;
    @Input() externalIntegration: boolean;
    @Input() public set payrollExportFormat(value: string) {
        this.exportFormGroup.controls['payrollCodeFormat'].setValue(value);
        this._payrollExportFormat = value;
        this.PayrollExportFormatSubject.next(value);
    }
    public get payrollExportFormat(): string {
        return this._payrollExportFormat;
    }
    private _items: ThirdPartyPayroll[];
    @Input() public set items(value: ThirdPartyPayroll[]) {
        this._items = value;
        this.itemsSubject.next(value);
    }
    public get items(): ThirdPartyPayroll[] {
        return this._items;
    }
    private fileFormatsSubject = new ReplaySubject<FileFormatModel[]>();
    private _fileFormats: FileFormatModel[];
    @Input() public set fileFormats(value: FileFormatModel[]) {
        this._fileFormats = value;
        this.fileFormatsSubject.next(value);
    }
    public get fileFormats(): FileFormatModel[] {
        return this._fileFormats;
    }
    private PayPeriodDatesSubject = new ReplaySubject<SelectItem[]>(1);
    private _payPeriodDates: SelectItem[];
    @Input() public set payPeriodDates(value: SelectItem[]) {
        this._payPeriodDates = value;
        this.PayPeriodDatesSubject.next(value);
    }
    public get payPeriodDates(): SelectItem[] {
        return this._payPeriodDates;
    }
    private StartDateSubject = new ReplaySubject<Date>(1);
    private _startDate: Date;
    @Input() public set startDate(value: Date) {
        this._startDate = value;
        this.StartDateSubject.next(value);
    }
    public get startDate(): Date {
        return this._startDate;
    }
    @Input() endDate: Date;
    @Output() UploadPayroll = new ReplaySubject<string>(1);
    cols: {
        field: string;
        header: string;
        include: boolean;
        seq: number;
    }[];
    moment: any;
    @Output() PayrollExportFormatChanged = new EventEmitter<string>();
    @Output() Export = new EventEmitter();
    exportFormGroup: UntypedFormGroup;
    dropdownItemHeight = environment.dropdownItemHeight;
    protected loading = false;
    private active = true;
    constructor(private log: LoggingService) {
        this.exportFormGroup = new UntypedFormGroup({
            scheduledDates: new UntypedFormControl(null, Validators.required),
            payrollCodeFormat: new UntypedFormControl(
                this.payrollExportFormat,
                Validators.required
            )
        });
        this.setupSubscriptions();
        this.moment = moment;
    }
    setupSubscriptions() {
        combineLatest([this.itemsSubject, this.fileFormatsSubject])
            .pipe(
                takeWhile(() => this.active),
                tap(() => (this.loading = false)),
                filter(
                    ([items, fileFormats]) =>
                        !!items &&
                        items.length > 0 &&
                        !!fileFormats &&
                        fileFormats.length > 0
                )
            )
            .subscribe(([items, fileFormats]) => {
                const keys = Object.keys(items[0] as any);
                this.cols = keys
                    .filter(k => k !== 'id')
                    .map(k => {
                        const ff = fileFormats.find(
                            x =>
                                x.Fieldname.toLocaleLowerCase() ===
                                k.toLocaleLowerCase()
                        );
                        return {
                            field: k,
                            header: ff ? ff.Colheader : k,
                            include: ff ? ff.Isstandard === true : true,
                            seq: ff ? ff.Sequencenbr : 99
                        };
                    })
                    .filter(c => c.include)
                    .sort((a, b) => a.seq - b.seq);
                this.log.verbose({
                    type: 'ThirdPartyPayrollExportComponent:cols',
                    items,
                    fileFormats,
                    cols: this.cols
                });
            });

        combineLatest([
            this.PayPeriodDatesSubject,
            this.StartDateSubject,
            this.PayrollExportFormatSubject
        ])
            .pipe(take(1))
            .subscribe(([dates, startDate]) => {
                if (startDate) {
                    this.log.verbose({
                        type: 'ThirdPartyPayrollExportComponent:PayPeriodDatesSubject,StartDateSubject,PayrollExportFormatSubject',
                        value: moment(startDate).format('MM/DD/YY'),
                        dates,
                        startDate
                    });
                    const controls = this.exportFormGroup.controls;
                    const payPeriod = dates.find(x =>
                        (x.label as string).startsWith(
                            moment(startDate).format('MM/DD/YY')
                        )
                    );
                    if (payPeriod) {
                        controls['scheduledDates'].setValue(payPeriod.value);
                        this.getPayrollExport();
                    }
                }
            });

        this.exportFormGroup.controls['payrollCodeFormat'].valueChanges
            .pipe(takeWhile(() => this.active))
            .subscribe(value => this.PayrollExportFormatChanged.emit(value));
    }

    ngOnInit(): void {}
    ngOnDestroy(): void {
        this.active = false;
    }
    ngAfterViewInit() {
        this.log.verbose({
            type: 'ThirdPartyPayrollExportComponent:ngAfterViewInit',
            startDate: this.startDate,
            endDate: this.endDate
        });
    }
    getPayrollExport() {
        this.loading = true;
        const controls = this.exportFormGroup.controls;
        // this.log.verbose({
        //     type: 'ThirdPartyPayrollExportComponent:getPayrollExport',
        //     controls: controls.value
        // });
        let codeAsRow = false;
        try {
            codeAsRow =
                (controls['payrollCodeFormat'].value as string)
                    ?.toLocaleLowerCase()
                    ?.localeCompare('row') === 0;
        } catch (error) {}
        this.Export.next({
            scheduledDateID: controls['scheduledDates'].value,
            codeAsRow
        });
    }
}
